import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Settings, Mail, FileDown, ShieldCheck, QrCode } from 'lucide-react';
import { moduleApi } from '../../../lib/api';
import { useAuth } from '../../../context/AuthContext';
import { resolveLogoUrl } from '../../../lib/resolveLogoUrl';
import { generateTechnicalSheetPdf } from '../../../lib/menusConvivesTechnicalSheetPdfGenerator';
import { generateClientRecapPdf } from '../../../lib/menusConvivesClientRecapPdfGenerator';
import { COLLECTE_STATUS_LABELS, COLLECTE_STATUS_ORDER } from '../collecteStatus';
import { ConvivesImportSection } from './ConvivesImportSection';
import { ConviveEmailSettingsModal } from './ConviveEmailSettingsModal';

interface EvenementielEvent { id: string; type: 'PRIVÉ' | 'PROFESSIONNEL'; first_name?: string; last_name?: string; company_name?: string; start_time: string; num_people?: number; client_id?: string; }
interface Collecte { status: string; production_locked_at: string | null; responses_closed_at?: string | null; }

const eventLabel = (e: EvenementielEvent) => e.type === 'PRIVÉ' ? `${e.first_name || ''} ${e.last_name || ''}`.trim() : (e.company_name || '');

/** Page d'atterrissage du module "Menus & Convives" pour un événement précis — tableau de suivi des
 *  convives/invitations/réponses. Volontairement séparée de la configuration du menu (EvenementielConvivesPage.tsx,
 *  route .../menu) : retour d'Emeni, les deux sur un seul écran faisaient "brouillon". */
export function ConvivesSuiviPage() {
    const { eventId } = useParams<{ eventId: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [event, setEvent] = React.useState<EvenementielEvent | null>(null);
    const [collecte, setCollecte] = React.useState<Collecte | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [showEmailSettings, setShowEmailSettings] = React.useState(false);
    const [downloadingSheet, setDownloadingSheet] = React.useState(false);
    const [includeAllergies, setIncludeAllergies] = React.useState(true);
    const [downloadingRecap, setDownloadingRecap] = React.useState(false);
    const [error, setError] = React.useState('');
    const load = React.useCallback(() => {
        if (!eventId) return;
        Promise.all([moduleApi.getEvenementielEvent(eventId), moduleApi.getEvenementielCollecte(eventId)])
            .then(([ev, col]) => {
                setEvent(ev);
                setCollecte(col);
            })
            .finally(() => setLoading(false));
    }, [eventId]);

    React.useEffect(() => { load(); }, [load]);

    // Fiche technique cuisine (Phase 5) — total par plat choisi + allergies/intolérances déclarées. Demande
    // explicite d'Emeni (2026-08-22).
    const downloadTechnicalSheet = async () => {
        if (!eventId || !event) return;
        setDownloadingSheet(true);
        setError('');
        try {
            const sheet: any = await moduleApi.getEvenementielCollecteTechnicalSheet(eventId);
            await generateTechnicalSheetPdf({
                eventLabel: eventLabel(event),
                eventDate: event.start_time,
                totalResponded: sheet?.total_responded || 0,
                dishes: Array.isArray(sheet?.dishes) ? sheet.dishes : [],
                restrictionsAllergies: Array.isArray(sheet?.restrictions_allergies) ? sheet.restrictions_allergies : [],
                issuerName: user?.company_name || undefined,
                logoUrl: resolveLogoUrl(user?.logoUrl || user?.logo_url) || null,
                brandColor: user?.theme_color || undefined,
                includeAllergies,
            });
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la génération de la fiche technique.');
        } finally {
            setDownloadingSheet(false);
        }
    };

    // Fiche récap CLIENT (Jour J) — ce que les convives ont choisi. Demande explicite d'Emeni (2026-08-22).
    const downloadClientRecap = async () => {
        if (!eventId || !event) return;
        setDownloadingRecap(true);
        setError('');
        try {
            const recap: any = await moduleApi.getEvenementielCollecteClientRecap(eventId);
            await generateClientRecapPdf({
                eventLabel: eventLabel(event),
                eventDate: event.start_time,
                guests: Array.isArray(recap?.guests) ? recap.guests.map((g: any) => ({ name: g.name, tableNumber: g.table_number, dishesByCategory: g.dishes_by_category || {}, allergies: g.allergies || [], restrictions: g.restrictions || [] })) : [],
                issuerName: user?.company_name || undefined,
                logoUrl: resolveLogoUrl(user?.logoUrl || user?.logo_url) || null,
                brandColor: user?.theme_color || undefined,
            });
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la génération du récap.');
        } finally {
            setDownloadingRecap(false);
        }
    };


    // La clôture a été déplacée sur la LISTE des événements (MenusConvivesListPage) : c'est là qu'on voit
    // les privatisations passées, et c'est de là que les trois documents se téléchargent une fois l'événement
    // clôturé. Cette page garde seulement les téléchargements individuels, utilisables à tout moment.
    // Demande d'Emeni (2026-08-23).

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;
    if (!event || !eventId) return <p className="text-sm text-content-muted">Événement introuvable.</p>;

    const statusIdx = COLLECTE_STATUS_ORDER.indexOf(collecte?.status || 'menus_a_configurer');
    const reachedResponsesTerminees = statusIdx >= COLLECTE_STATUS_ORDER.indexOf('reponses_terminees');
    const responsesClosedOrTerminated = reachedResponsesTerminees || !!collecte?.responses_closed_at;

    return (
        // Pas de largeur maximale ici, contrairement aux autres ecrans : c'est une liste de plusieurs
        // centaines de fiches, elle n'a rien a gagner a etre bridee au milieu d'un ecran d'ordinateur.
        // Signale par Emeni (2026-08-24) : "on dirait qu'il est en version telephone alors que je suis sur PC".
        <div className="space-y-6">
            <div>
                <button onClick={() => navigate('/menus-convives')} className="flex items-center gap-1.5 text-xs font-bold text-content-muted hover:text-content mb-3">
                    <ArrowLeft size={14} /> Retour à la liste
                </button>
                {/* Validation & production remontée tout en haut à droite — avec 350 convives, ce bloc se
                    perdait complètement en bas de l'écran, sous une longue liste. Demande explicite d'Emeni
                    (2026-08-23) : "il faudrait le mettre tout en haut sur la droite, il y a de la place". */}
                <div className="grid grid-cols-1 lg:grid-cols-[1fr,auto] gap-3 items-start">
                    <div className="space-y-3">
                        <div>
                            <h1 className="text-xl font-bold text-content tracking-tight">{eventLabel(event)}</h1>
                            <p className="text-content-muted text-sm mt-0.5">
                                {new Date(event.start_time).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </p>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-black uppercase px-3 py-1.5 rounded-lg bg-brand-500/10 text-brand-600 dark:text-brand-400 border border-brand-500/20">
                                {COLLECTE_STATUS_LABELS[collecte?.status || 'menus_a_configurer']}
                            </span>
                            <Link to={`/menus-convives/${eventId}/qr-codes`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-600 text-brand-fg hover:bg-brand-700 text-xs font-black uppercase tracking-wide">
                                <QrCode size={14} /> QR codes
                            </Link>
                            {/* Documents toujours accessibles, même avant la clôture des réponses : ils étaient
                                masqués jusqu'à ce que la collecte soit close, alors qu'une cuisine a besoin
                                d'anticiper ses quantités bien avant. Un repère « partiel » signale que tous
                                les convives n'ont pas encore répondu, ce qui vaut mieux que pas de document
                                du tout. Signalé par Emeni (2026-08-23). */}
                            {!responsesClosedOrTerminated && (
                                <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 px-2 py-1 rounded bg-amber-500/10 border border-amber-500/30">
                                    Documents partiels — réponses en cours
                                </span>
                            )}
                            {(
                                <>
                                    <button type="button" onClick={downloadTechnicalSheet} disabled={downloadingSheet}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border border-border text-content hover:bg-surface transition-colors disabled:opacity-50">
                                        {downloadingSheet ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />} Fiche technique cuisine
                                    </button>
                                    <label className="flex items-center gap-1.5 text-[11px] text-content-muted cursor-pointer" title="Décocher pour générer la fiche sans afficher les allergies déclarées">
                                        <input type="checkbox" checked={includeAllergies} onChange={(e) => setIncludeAllergies(e.target.checked)}
                                            className="w-3.5 h-3.5 rounded border-border accent-brand-600" />
                                        Allergies
                                    </label>
                                    <button type="button" onClick={downloadClientRecap} disabled={downloadingRecap}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border border-border text-content hover:bg-surface transition-colors disabled:opacity-50">
                                        {downloadingRecap ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />} Récap convives (client)
                                    </button>
                                </>
                            )}
                            <Link to={`/menus-convives/${eventId}/menu`} title="Configurer le menu"
                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-surface-soft border border-border text-content-muted hover:text-content text-xs font-bold">
                                <Settings size={13} />
                            </Link>
                            <button onClick={() => setShowEmailSettings(true)} title="Personnaliser les emails des convives"
                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-surface-soft border border-border text-content-muted hover:text-content text-xs font-bold">
                                <Mail size={13} />
                            </button>
                        </div>
                    </div>

                </div>
            </div>

            {showEmailSettings && <ConviveEmailSettingsModal onClose={() => setShowEmailSettings(false)} />}

            {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}

            <ConvivesImportSection eventId={eventId} />
        </div>
    );
}
